import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { ImportBranchDto, InboundTripDto, InboundTripImportBranchDto, PortOfEntryDto, PortOfLadingDto } from 'dtos'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE } from 'localConstants'
import { InboundTripImportBranchValidationSchema } from 'dtos/inboundTripImportBranchDto'

interface ImportBranchAddEditDialogProps {
  initialValues: InboundTripImportBranchDto
  inboundTrip: InboundTripDto
  open: boolean
  isLoading: boolean
  onClose: () => void
  onSave: (values: InboundTripImportBranchDto) => void
}

// TODO: This should be called InboundTripImportBranchEditDialog.
// Maxwell Sommer 09/11/2024
export default function ImportBranchAddEditDialog({
  initialValues,
  inboundTrip,
  onClose,
  onSave,
  open,
  isLoading
}: ImportBranchAddEditDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingImportBranches: false,
    isGettingPortsOfLading: false,
    isGettingPortsOfEntry: false
  })

  const [importBranches, setImportBranches] = useState<ImportBranchDto[]>([])
  const [portsOfLading, setPortsOfLading] = useState<PortOfLadingDto[]>([])
  const [portsOfEntry, setPortsOfEntry] = useState<PortOfEntryDto[]>([])

  useEffect(() => {
    setLoadingState('isGettingImportBranches', true)
    controllers
      .getImportBranches()
      .then(response => {
        setImportBranches(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingImportBranches', false)
      })
  }, [])

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={InboundTripImportBranchValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Edit Import Branch</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='Inbound Trip #' value={inboundTrip.inboundTripNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={option => option.name!}
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('importBranch', value)
                        setFieldValue('portOfLading', undefined)
                        setFieldValue('portOfEntry', undefined)

                        if (value?.importBranchGuid) {
                          setLoadingState('isGettingPortsOfLading', true)
                          controllers
                            .getPortOfLading({
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              importBranchGuid: value.importBranchGuid
                            })
                            .then(response => {
                              setPortsOfLading(response.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingPortsOfLading', false)
                            })

                          setLoadingState('isGettingPortsOfEntry', true)
                          controllers
                            .getPortOfEntry({
                              page: 0,
                              pageSize: AUTOCOMPLETE_PAGESIZE,
                              importBranchGuid: value.importBranchGuid
                            })
                            .then(response => {
                              setPortsOfEntry(response.value)
                            })
                            .finally(() => {
                              setLoadingState('isGettingPortsOfEntry', false)
                            })
                        }
                      }}
                      options={importBranches}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.importBranch && errors.importBranch)}
                          helperText={touched.importBranch && errors.importBranch}
                          label='Import Branch'
                        />
                      )}
                      value={values.importBranch}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={portOfLading => portOfLading.cityProvince!}
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('portOfLading', value)
                      }}
                      options={portsOfLading.map(portOfLading => ({
                        portOfLadingGuid: portOfLading.portOfLadingGuid,
                        cityProvince: portOfLading.cityProvince,
                        isActive: portOfLading.isActive
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.portOfLading && errors.portOfLading)}
                          helperText={touched.portOfLading && errors.portOfLading}
                          label='Port Of Lading'
                        />
                      )}
                      value={
                        values.portOfLading
                          ? {
                              portOfLadingGuid: values.portOfLading.portOfLadingGuid,
                              cityProvince: values.portOfLading.cityProvince,
                              isActive: values.portOfLading.isActive
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={portOfEntry => portOfEntry.cityState!}
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('portOfEntry', value)
                      }}
                      options={portsOfEntry.map(portOfEntry => ({
                        portOfEntryGuid: portOfEntry.portOfEntryGuid,
                        cityState: portOfEntry.cityState,
                        portCode: portOfEntry.portCode,
                        isActive: portOfEntry.isActive
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.portOfEntry && errors.portOfEntry)}
                          helperText={touched.portOfEntry && errors.portOfEntry}
                          label='Port Of Entry'
                        />
                      )}
                      value={
                        values.portOfEntry
                          ? {
                              portOfEntryGuid: values.portOfEntry.portOfEntryGuid,
                              cityState: values.portOfEntry.cityState,
                              portCode: values.portOfEntry.portCode,
                              isActive: values.portOfEntry.isActive
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Port Code' value={values.portOfEntry?.portCode} />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Import Branches are hardcoded into VTS2: HQ Location, Flatrock Location, and Columbus Location. || Port
                      of Lading dropdown is populated based on the Ports of Lading Maintenance card under Settings. This
                      cascading dropdown is based on the selected Import Branch. || Port of Entry dropdown is populated based on
                      the Ports of Entry Maintenance card under Settings. This cascading dropdown is based on the selected
                      Import Branch. || Port Code is a label that is populated based on the select Port of Entry. || If a Port
                      of Lading or Port of Entry are toggled off for an Import Branch under Settings, those respective ports
                      will still be associated with any existing Inbound Trips but will not be able to be added to new trips.||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton
                  disabled={inboundTrip.isLocked}
                  loading={isLoading || isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
